import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import type { RootState } from "../../store/Index";
import { useThemeColors } from "../../hooks/useThemeColors";
import { commentsApi, ItemKind, JobCardComment } from "./jobCardApi";
import { errorMessage } from "./jobCardShared";
import { Chip } from "../../components/ui";

const ACTION_CHIP: Record<
  Exclude<JobCardComment["action"], "comment">,
  "success" | "warning" | "error"
> = {
  approved: "success",
  rejected: "error",
  needs_revision: "warning",
};

const ACTION_LABEL: Record<Exclude<JobCardComment["action"], "comment">, string> = {
  approved: "Approved",
  rejected: "Rejected",
  needs_revision: "Requested revision",
};

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const CommentThread = ({ kind, itemId }: { kind: ItemKind; itemId: number }) => {
  const colors = useThemeColors();
  const user = useSelector((state: RootState) => state.auth.user);
  const [comments, setComments] = useState<JobCardComment[]>([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [postError, setPostError] = useState<string | null>(null);

  const loadComments = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setComments(await commentsApi.list(kind, itemId));
    } catch (error) {
      setLoadError(errorMessage(error, "Could not load comments."));
    } finally {
      setLoading(false);
    }
  }, [kind, itemId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const postComment = async () => {
    const body = draft.trim();
    if (!body) return;

    setPosting(true);
    setPostError(null);
    try {
      const created = await commentsApi.create(kind, itemId, body);
      setComments((current) => [...current, created]);
      setDraft("");
    } catch (error) {
      setPostError(errorMessage(error, "Could not post this comment. Please try again."));
    } finally {
      setPosting(false);
    }
  };

  const currentName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.username || "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: colors.primary[100] }}>
        Comments
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadComments}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : comments.length === 0 ? (
        <Typography sx={{ fontSize: "0.82rem", color: colors.primary[300] }}>
          No comments yet.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, maxHeight: 260, overflowY: "auto" }}>
          {comments.map((comment) => {
            const authorName = comment.author_details?.full_name || "Unknown";
            const isMine = comment.author_details?.id === user?.id;
            const action = comment.action === "comment" ? null : comment.action;
            return (
              <Box
                key={comment.id}
                sx={{
                  display: "flex",
                  gap: 1,
                  alignSelf: isMine ? "flex-end" : "flex-start",
                  flexDirection: isMine ? "row-reverse" : "row",
                  maxWidth: "92%",
                }}
              >
                <Avatar
                  src={comment.author_details?.profile_picture ?? undefined}
                  sx={{ width: 30, height: 30, fontSize: "0.68rem", flexShrink: 0 }}
                >
                  {initials(authorName)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      flexWrap: "wrap",
                      flexDirection: isMine ? "row-reverse" : "row",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: colors.primary[100] }}>
                      {isMine ? "You" : authorName}
                    </Typography>
                    {action && (
                      <Chip
                        status={ACTION_CHIP[action]}
                        label={ACTION_LABEL[action]}
                        sx={{ height: 19, fontSize: "0.63rem" }}
                      />
                    )}
                    <Typography sx={{ fontSize: "0.68rem", color: colors.primary[300] }}>
                      {new Date(comment.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      mt: 0.4,
                      px: 1.25,
                      py: 0.9,
                      borderRadius: "12px",
                      backgroundColor: isMine ? colors.primary[800] : `${colors.primary[300]}15`,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.82rem", color: colors.primary[100], whiteSpace: "pre-wrap" }}>
                      {comment.body}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {postError && <Alert severity="error">{postError}</Alert>}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
        <Avatar
          src={user?.image_url ?? undefined}
          sx={{ width: 30, height: 30, fontSize: "0.68rem", flexShrink: 0 }}
        >
          {initials(currentName)}
        </Avatar>
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          size="small"
          placeholder="Add a comment..."
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          disabled={posting}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "0.85rem",
              color: colors.primary[100],
            },
          }}
        />
        <Button
          variant="contained"
          onClick={postComment}
          disabled={!draft.trim() || posting}
          sx={{ textTransform: "none", borderRadius: "20px", minWidth: 70 }}
        >
          {posting ? <CircularProgress size={18} color="inherit" /> : "Post"}
        </Button>
      </Box>
    </Box>
  );
};

export default CommentThread;
